import React, { useEffect, useRef, useState } from "react";
import styles from "./index.module.scss";

interface ScratchCanvasProps {
  width: number;
  height: number;
  reward: React.ReactNode;
  onComplete?: () => void;
  finishPercent?: number;
  coverColor?: string;
  coverImage?: string;
}

const ScratchCanvas: React.FC<ScratchCanvasProps> = ({
  width,
  height,
  reward,
  onComplete,
  finishPercent = 60,
  coverColor = "#d9d9d9",
  coverImage,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // --- initialize cover layer ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (coverImage) {
      // use image overlay
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = coverImage;
      img.onload = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
      };
      img.onerror = () => {
        ctx.fillStyle = coverColor;
        ctx.fillRect(0, 0, width, height);
      };
    } else {
      // generate text pattern overlay
      const patternCanvas = document.createElement("canvas");
      const pctx = patternCanvas.getContext("2d")!;
      patternCanvas.width = 55;
      patternCanvas.height = 45;

      // base color
      pctx.fillStyle = coverColor;
      pctx.fillRect(0, 0, patternCanvas.width, patternCanvas.height);

      // rotated text
      pctx.save();
      pctx.translate(patternCanvas.width / 2, patternCanvas.height / 2);
      pctx.rotate((-40 * Math.PI) / 180);
      pctx.fillStyle = "#555";
      pctx.font = "8px sans-serif";
      pctx.textAlign = "center";
      pctx.textBaseline = "middle";
      pctx.fillText("SCRATCH & WIN", 0, 0);
      pctx.restore();

      const pattern = ctx.createPattern(patternCanvas, "repeat");
      if (pattern) {
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, width, height);
      }
    }
  }, [coverImage, coverColor, width, height]);

  // --- handle scratching ---
  const handlePointerDown = () => setIsDrawing(true);
  const handlePointerUp = () => {
    setIsDrawing(false);
    checkScratchCompletion();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, 20, 0, Math.PI * 2, false);
    ctx.fill();
  };

  // --- check completion ---
  const checkScratchCompletion = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const imageData = ctx.getImageData(0, 0, width, height);
    let cleared = 0;
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] === 0) cleared++;
    }

    const percent = (cleared / (width * height)) * 100;
    if (percent >= finishPercent && onComplete) {
      onComplete();
    }
  };

  return (
    <div className={styles.scratchCardArea} style={{ width, height }}>
      <div className={styles.rewardImage}>{reward}</div>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className={styles.scratchCanvas}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerUp}
      />
    </div>
  );
};

export default ScratchCanvas;
